import { useRef, ReactNode, type FC } from "react";
import { motion, useTransform } from "framer-motion";
import { useScrollProgress } from "@tennr/frontend/hooks/useScrollProgress";
import "./HorizontalMarquee.css";
import { lightenColor } from "@tennr/frontend/utils/lightenColor";

interface HorizontalMarqueeProps {
  children: ReactNode;
  widthMode?: "full" | "center" | "default";
  verticalOnMobile?: boolean;
  autoscroll?: boolean;
  autoscrollSpeed?: number;
  gap?: number;
}

const baseColor = "#2a2c31";
const lightColor = lightenColor(baseColor, 20); // 20% lighter
const darkColor = lightenColor(baseColor, -20); // 20% darker
export const HorizontalMarquee: FC<HorizontalMarqueeProps> = ({
  children,
  widthMode = "default",
  verticalOnMobile = false,
  autoscroll = false,
  autoscrollSpeed = 0.5,
  gap = 14,
}) => {
  const el = useRef<HTMLElement>(null!);
  const { progress } = useScrollProgress({ target: el });

  const xOffsets =
    widthMode === "full"
      ? ["10vw", "5vw", "0vw", "-5vw"]
      : widthMode === "center"
        ? ["0vw", "-5vw", "-10vw", "-15vw"]
        : ["3vw", "1vw", "0vw", "0vw"];

  // calculate scroll-based transform only if not in autoscroll mode
  const scrollX = useTransform(progress, [0, 0.2, 0.4, 1], xOffsets);
  const opacity = useTransform(progress, [0, 0.2, 0.4, 1], [0, 1, 1, 1]);
  const color = useTransform(progress, [0, 0.2, 0.4, 1], [lightColor, darkColor, darkColor, baseColor]);

  const isMobile = window.innerWidth <= 768; // Simple mobile check

  // use animation frame to handle automatic scrolling
  // calculate CSS animation properties based on autoscrollSpeed
  const animationDuration = autoscrollSpeed ? Math.max(5, 120 - autoscrollSpeed * 115) : 120; // 5-120s range

  // determine which x-transform to use based on autoscroll setting
  // when in autoscroll mode, we'll handle the animation with CSS
  const x = autoscroll ? 0 : scrollX;

  const marqueeClassNames = `horizontal-marquee ${verticalOnMobile && isMobile ? "vertical-on-mobile" : ""} ${
    autoscroll ? "autoscroll" : ""
  }`;

  return (
    <section ref={el} className={marqueeClassNames}>
      <motion.div
        style={
          {
            x,
            opacity,
            translateX: widthMode === "full" ? "-50vw" : widthMode === "center" ? "10vw" : "0",
            // animate the CSS variable
            "--horizontal-marquee-color": color as any,
            ...(autoscroll ? { animationDuration: `${animationDuration}s` } : {}),
            gap: gap,
          } as any
        }
        className="marquee__content"
      >
        {children}
        {/* Duplicate content for smooth infinite scrolling when autoscroll is enabled */}
        {autoscroll && <div className="duplicated-content">{children}</div>}
      </motion.div>
    </section>
  );
};
